import { AlertCircle, Check, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { NonAuthSidebar } from '@/components/layouts/Sidebar/NonAuthSidebar';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCheckPasswordStrength, useResetPasswordWithSignature } from '@/hooks/useAuth';

/** Extract a user-facing error message from the password-reset mutation error. */
function getResetPasswordError(error: unknown): string {
  const err = error as { response?: { data?: { message?: string; error?: string } } };
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    'Failed to reset password. The link may have expired.'
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const sig = searchParams.get('sig') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const mutation = useResetPasswordWithSignature();
  const { strength, isChecking, checkStrength, meetsRequirements } = useCheckPasswordStrength(300);

  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmit =
    sig &&
    password &&
    confirmPassword &&
    passwordsMatch &&
    meetsRequirements &&
    !mutation.isPending;

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (value) {
      checkStrength(value, []);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate({ sig, password, confirm_password: confirmPassword });
  };

  if (!sig) {
    return (
      <div className="flex h-screen overflow-hidden">
        <NonAuthSidebar />
        <div className="flex flex-1 items-center justify-center px-4">
          <div className="w-full max-w-md space-y-6 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
            <h2 className="text-2xl font-semibold">Invalid Reset Link</h2>
            <p className="text-muted-foreground">
              This password reset link is invalid or has expired.
            </p>
            <Link to="/login">
              <Button variant="outline" className="mt-4">
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (mutation.isSuccess) {
    return (
      <div className="flex h-screen overflow-hidden">
        <NonAuthSidebar />
        <div className="flex flex-1 items-center justify-center px-4">
          <div className="w-full max-w-md space-y-6 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <h2 className="text-2xl font-semibold">Password Reset</h2>
            <p className="text-muted-foreground">
              Your password has been reset successfully. You can now log in.
            </p>
            <Link to="/login">
              <Button className="mt-4">Go to Login</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <NonAuthSidebar />
      <div className="flex flex-1 items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-semibold tracking-tight">Reset your password</h2>
            <p className="mt-2 text-sm text-muted-foreground">Enter your new password below.</p>
          </div>

          {mutation.isError && (
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <p>{getResetPasswordError(mutation.error)}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                New Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => handlePasswordChange(e.target.value)}
                  placeholder="Enter new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {password && (
                <PasswordStrengthIndicator strength={strength} isChecking={isChecking} />
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirm Password
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword && (
                <div className="flex items-center gap-1.5 text-xs">
                  {passwordsMatch ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-green-500" />
                      <span className="text-green-500">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                      <span className="text-destructive">Passwords do not match</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={!canSubmit}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>

          <div className="text-center text-sm">
            <Link to="/login" className="text-primary hover:underline">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
